// «Как в Word»: клавиатура основного текста (core/editor/wordKeys.ts), тип текущей строки в строке состояния,
// подсказка «Как писать» в пустом документе, выравнивание граф таблиц по умолчанию - как у writer (чтобы таблица
// при наборе выглядела так же, как в DOCX).
import { useEffect, useState } from 'react';
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, Selection, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';
import type { RegistryApi } from '../../core/registry';
import { useApp } from '../../core/store';
import { wordKeys } from '../../core/editor/wordKeys';
import { currentLine, onLineFocus } from '../../core/editor/lineKind';
import { USE_NBSP } from '../../core/schema/constants';
import './typing.css';

// ------------------------------------------------------------------------------------------------ таблицы
// writer/builder.py emit_table + lib/sto_lib.format_table: боковик (1-я графа) - влево (по центру, если в нём только
// числа), значения показателей - по центру графы, графа с длинным текстом (в среднем > 40 знаков) - влево; прочерк -
// по центру; боковик прижат к верху ячейки, остальные - по центру строки по высоте. Явное выравнивание ячейки
// (атрибут align) - как задано (правило CSS [data-align]).
const NUMERIC = /^[\d\s.,–−\-+%×x/:()<>≤≥±]+$/;
const DASHES = new Set(['-', '–', '—']);

function tableDecos(table: PMNode, start: number, out: Decoration[]) {
  let map: TableMap;
  try { map = TableMap.get(table); } catch { return; }
  // головка - ведущие строки из tableHeader (как convert/nodes_objects.py), не меньше одной строки (writer)
  let hdr = 0;
  for (let i = 0; i < table.childCount; i++) {
    const row = table.child(i);
    let all = row.childCount > 0;
    row.forEach((c) => { if (c.type.name !== 'tableHeader') all = false; });
    if (!all) break;
    hdr++;
  }
  hdr = Math.max(1, Math.min(hdr, table.childCount));
  const cells: { pos: number; col: number; row: number; node: PMNode; text: string }[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < map.map.length; i++) {
    const rel = map.map[i];
    if (seen.has(rel)) continue;
    seen.add(rel);
    const node = table.nodeAt(rel);
    if (!node) continue;
    const r = map.findCell(rel);
    cells.push({ pos: start + 1 + rel, col: r.left, row: r.top, node, text: node.textContent.replace(/[`*$]/g, '').trim() });
  }
  const vals = new Map<number, string[]>();
  for (const c of cells) {
    if (c.row < hdr || !c.text || DASHES.has(c.text)) continue;
    if (!vals.has(c.col)) vals.set(c.col, []);
    vals.get(c.col)!.push(c.text);
  }
  const al = (col: number) => {
    const v = vals.get(col) || [];
    const numeric = v.every((x) => NUMERIC.test(x));
    if (col === 0) return v.length && numeric ? 'center' : 'left';
    const longtxt = v.length && !numeric && v.reduce((a, x) => a + x.length, 0) / v.length > 40;
    return longtxt ? 'left' : 'center';
  };
  for (const c of cells) {
    if (c.row < hdr || c.node.type.name !== 'tableCell') continue;
    const cls: string[] = [];
    if (c.col === 0) cls.push('sto-cell-side');
    if (!c.node.attrs.align) cls.push(DASHES.has(c.text) ? 'sto-al-center' : 'sto-al-' + al(c.col));
    out.push(Decoration.node(c.pos, c.pos + c.node.nodeSize, { class: cls.join(' ') }));
  }
}

const alignKey = new PluginKey<DecorationSet>('stoTableAlign');
function alignSet(doc: PMNode): DecorationSet {
  const out: Decoration[] = [];
  doc.descendants((n, pos) => {
    if (n.type.name === 'table') { tableDecos(n, pos, out); return false; }
    return !n.isTextblock;
  });
  return DecorationSet.create(doc, out);
}
const TableAlignExtension = Extension.create({
  name: 'stoTableAlign',
  addProseMirrorPlugins() {
    return [new Plugin({
      key: alignKey,
      state: { init: (_, s) => alignSet(s.doc), apply: (tr, prev) => (tr.docChanged ? alignSet(tr.doc) : prev) },
      props: { decorations: (s) => alignKey.getState(s) },
    })];
  },
});

// ------------------------------------------------------------------------------------------------ «Как писать»
export const HOWTO: [string, string][] = [
  ['Enter', 'новый абзац - отступ 1,25 см и выравнивание по ширине ставятся сами'],
  ['Shift+Enter', 'новая строка в том же абзаце'],
  ['Ctrl+Alt+1 / 2 / 3', 'раздел / подраздел / пункт (или «# », «## », «### » в начале строки), Ctrl+Alt+0 - обычный текст'],
  ['«- » в начале строки', 'перечисление с дефисом; «а) » - с буквами; Ctrl+Z сразу после - оставить набранное как есть'],
  ['Tab / Shift+Tab', 'уровень перечисления ниже / выше; в таблице - следующая / предыдущая ячейка'],
  ['Enter на пустой позиции', 'закончить перечисление; Backspace в начале позиции - снять маркер'],
  ['Ctrl+Enter', 'разрыв страницы'],
  ...(USE_NBSP ? [['Ctrl+Shift+Пробел', 'неразрывный пробел («рисунке 3», «5 кг»); Ctrl+Shift+- - неразрывный дефис']] as [string, string][] : []),
  ['Ctrl+B / I / U', 'полужирный / курсив / подчёркнутый; Ctrl+= - нижний индекс, Ctrl+Shift+= - верхний'],
  ['Ctrl+V', 'вставить из Word, Excel, браузера; скриншот - сразу рисунком с подписью'],
  ['Alt+=', 'формула; Ctrl+Alt+T - таблица; Ctrl+Alt+L - листинг; @ - ссылка на рисунок, таблицу, источник'],
  ['Ctrl+Shift+8', 'показать непечатаемые знаки (¶, пробелы, неразрывные пробелы °, разрывы строк ↵)'],
  ['Вид → Как при печати', 'лист без подсказок и подчёркиваний - ровно как в PDF'],
  ['F1 · Ctrl+/', 'памятка по СТО · все сочетания клавиш'],
];

function howtoWidget(view: EditorView): HTMLElement {
  const el = document.createElement('div');
  el.className = 'sto-howto';
  el.contentEditable = 'false';
  el.setAttribute('data-testid', 'howto-hint');
  const h = document.createElement('div');
  h.className = 'sto-howto-head';
  h.textContent = 'Как писать: как в Word - что видите на листе, то и будет в DOCX и PDF';
  el.appendChild(h);
  for (const [k, d] of HOWTO) {
    const row = document.createElement('div');
    row.className = 'sto-howto-row';
    const kb = document.createElement('b');
    kb.textContent = k;
    row.appendChild(kb);
    row.appendChild(document.createTextNode(' - ' + d));
    el.appendChild(row);
  }
  // щелчок по подсказке - курсор в пустой абзац (подсказка не мешает начать писать)
  el.addEventListener('mousedown', (e) => { e.preventDefault(); view.focus(); });
  return el;
}

/** Документ без текста (пустой или шаблон «Структура по СТО» с пустыми абзацами под заголовками): позиция
 *  после первого пустого абзаца верхнего уровня (туда встаёт карточка «Как писать»), иначе -1. */
function howtoPos(doc: PMNode): number {
  if (doc.textContent.trim()) return -1;
  let hasObj = false;
  doc.descendants((n) => { if (['figure', 'stoTable', 'listing', 'formula'].includes(n.type.name)) hasObj = true; return !hasObj; });
  if (hasObj) return -1;
  let at = -1;
  doc.forEach((n, off) => { if (at < 0 && n.type.name === 'paragraph' && n.content.size === 0) at = off + n.nodeSize; });
  return at;
}
const howtoKey = new PluginKey<DecorationSet>('stoHowto');
const HowtoExtension = Extension.create({
  name: 'stoHowto',
  addProseMirrorPlugins() {
    const make = (doc: PMNode) => {
      const at = howtoPos(doc);
      return at >= 0 ? DecorationSet.create(doc, [Decoration.widget(at, howtoWidget, { side: -1, key: 'howto', ignoreSelection: true })]) : DecorationSet.empty;
    };
    return [new Plugin({
      key: howtoKey,
      state: { init: (_, s) => make(s.doc), apply: (tr, prev) => (tr.docChanged ? make(tr.doc) : prev) },
      props: { decorations: (s) => howtoKey.getState(s) },
    })];
  },
});

// ------------------------------------------------------------------------------------------------ начало работы
/** При открытии нового отчёта (текста ещё нет) - курсор в первый пустой абзац (под «Введение» / первый раздел)
 *  с прокруткой к нему: раньше редактор открывался без фокуса, и набор пропадал молча. Готовые отчёты открываются
 *  как раньше. Если фокус уже в окне или поле или курсор уже поставили - не трогаем. */
function startWriting(ed: Editor) {
  let tries = 0;
  const sel0 = ed.state.selection;
  const go = () => {
    if (ed.isDestroyed) return;
    if (!ed.state.selection.eq(sel0)) return;               // курсор уже поставили (щелчок, переход к находке, тест)
    const st = (window as any).stoPages?.state?.();
    if (st && !st.ready && tries++ < 60) { window.setTimeout(go, 50); return; }
    const ae = document.activeElement as HTMLElement | null;
    if (ae && ae !== document.body && !ed.view.dom.contains(ae)
      && (ae.closest('[role=dialog], .modal, .popover') || ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName) || ae.isContentEditable)) return;
    if (ed.view.hasFocus()) return;
    const doc = ed.state.doc;
    const blank = !doc.textContent.trim();
    if (!blank) return;                                     // открыт готовый отчёт - вид и фокус не трогаем
    let pos = -1;
    if (blank) doc.forEach((n, off) => { if (pos < 0 && n.type.name === 'paragraph') pos = off + 1; });
    let sel: Selection;
    try { sel = pos >= 0 ? TextSelection.create(doc, pos) : Selection.atStart(doc); } catch { sel = Selection.atStart(doc); }
    ed.view.dispatch(ed.state.tr.setSelection(sel).setMeta('addToHistory', false));
    ed.view.focus();
    if (blank && pos >= 0) {
      requestAnimationFrame(() => {
        try {
          const c = ed.view.coordsAtPos(ed.state.selection.head);
          const cv = document.querySelector('.canvas') as HTMLElement | null;
          if (!cv) return;
          const r = cv.getBoundingClientRect();
          cv.scrollBy(0, c.top - r.top - r.height * 0.3);
        } catch { /* нет координат */ }
      });
    }
  };
  window.setTimeout(go, 0);
}

// ------------------------------------------------------------------------------------------------ строка состояния
function LineStatus() {
  const editor = useApp((s) => s.editor);
  const [, setN] = useState(0);
  useEffect(() => {
    let raf = 0;
    const f = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setN((x) => x + 1)); };
    const off = onLineFocus(f);
    editor?.on('selectionUpdate', f);
    editor?.on('update', f);
    editor?.on('focus', f);
    return () => { cancelAnimationFrame(raf); off(); editor?.off('selectionUpdate', f); editor?.off('update', f); editor?.off('focus', f); };
  }, [editor]);
  const k = currentLine(editor);
  if (!k) return null;
  return (
    <span className="status-item status-line" data-testid="status-line" data-tip={(k.where ? k.where + ': ' : '') + k.label}
      data-tip-desc={k.fmt || undefined}>
      {k.where ? <span className="status-dim">{k.where}: </span> : null}{k.label}
    </span>
  );
}

export default function setup(r: RegistryApi) {
  r.extensions.add({ id: 'typing.keys', order: 5, create: () => wordKeys() });
  r.extensions.add({ id: 'typing.tableAlign', order: 60, create: () => TableAlignExtension });
  r.extensions.add({ id: 'typing.howto', order: 61, create: () => HowtoExtension });
  r.statusbar.add({ id: 'line', side: 'left', order: 25, component: LineStatus });
  r.events.on('editor:created', (ed: any) => startWriting(ed as Editor));
}
